import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  HomeIcon,
  ShoppingCartIcon,
  UsersIcon,
  DocumentTextIcon,
  PhotoIcon,
  ReceiptPercentIcon,
  GiftIcon,
  UserGroupIcon,
  NewspaperIcon,
  TrophyIcon,
  CurrencyDollarIcon,
  MegaphoneIcon,
  BuildingOffice2Icon,
} from "@heroicons/react/24/outline";
import {
  fetchOrders,
  getOrdersByCompanyId,
} from "../../features/admin/OrderSlice";

import { getCompanyByUserId } from "../../features/admin/CompanySlice";
import { Link } from "react-router-dom";
import { formatDate, formatCurrencyVND } from "../../utils/helps";

const years = Array.from(
  { length: 10 },
  (_, i) => new Date().getFullYear() - i
);

const Dashboard = () => {
  const dispatch = useDispatch();
  const [currentDate] = useState(new Date().toLocaleDateString("vi-VN"));
  const { partners, status, error } = useSelector((state) => state.partners);
  const users = useSelector((state) => state.users?.users?.data?.users);
  const { authPosts: posts } = useSelector((state) => state.posts);
  const { authNotifications: notifications } = useSelector(
    (state) => state.posts
  );
  const { orders } = useSelector((state) => state.orders);
  const [company, setCompany] = useState(0);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const userId = localStorage.getItem("userId");
  useEffect(() => {
    getCompanyInfo();
    getOrderData(userId);
  }, []);

  const getCompanyInfo = async () => {
    const companyData = await dispatch(getCompanyByUserId(userId)).unwrap();
    setCompany(companyData);
  };

  const getOrderData = async (id) => {
    if (hasPermission("BILL_SHOW")) {
      await dispatch(fetchOrders()).unwrap();
      return;
    }

    await dispatch(getOrdersByCompanyId(id)).unwrap();
  };

  // Mô phỏng dữ liệu thống kê
  const stats = [
    {
      id: 1,
      name: "Ban điều hành",
      value: users?.filter((user) => user.type === "admin").length || 0,
      icon: <UserGroupIcon className="h-8 w-8 text-primary" />,
      color: "bg-primary/10",
      link: "/admin/users",
      permission: () => hasPermission("USER_SHOW_LIST"),
    },
    {
      id: 2,
      name: "Tổng số hội viên",
      value: partners?.length || 0,
      icon: <UsersIcon className="h-8 w-8 text-accent" />,
      color: "bg-accent/10",
      link: "/admin/partner",
      permission: () => hasPermission("USER_SHOW"),
    },
    {
      id: 3,
      name: "Bài viết",
      value: posts?.length,
      icon: <NewspaperIcon className="h-8 w-8 text-error" />,
      color: "bg-error/10",
      link: "/admin/posts",
      permission: () => hasPermission("POST_SHOW"),
    },
  ];

  // Danh sách chức năng quản lý
  const features = [
    {
      id: 1,
      name: "Sản phẩm",
      icon: <ShoppingCartIcon className="h-6 w-6" />,
      link: "/admin/product",
    },
    {
      id: 2,
      name: "Giao dịch",
      icon: <ReceiptPercentIcon className="h-6 w-6" />,
      link: "/admin/order",
    },
    {
      id: 3,
      name: "Hội viên",
      icon: <UsersIcon className="h-6 w-6" />,
      link: "/admin/partner",
    },
    {
      id: 4,
      name: "Bài viết",
      icon: <DocumentTextIcon className="h-6 w-6" />,
      link: "/admin/posts",
    },
    {
      id: 5,
      name: "Media",
      icon: <PhotoIcon className="h-6 w-6" />,
      link: "/admin/media",
    },
    {
      id: 7,
      name: "Mã thưởng",
      icon: <GiftIcon className="h-6 w-6" />,
      link: "/coupon",
    },
  ];

  const getFiltedOrders = () => {
    if (!orders || orders.length < 1) return [];
    else {
      return orders.filter((order) => {
        const orderYear = new Date(order.createdAt).getFullYear();
        return orderYear === selectedYear;
      });
    }
  };

  const stats2 = [
    {
      id: 4,
      name: "Số lượng giao dịch",
      value: getFiltedOrders().length || 0,
      icon: <ReceiptPercentIcon className="h-8 w-8 text-primary" />,
      color: "bg-primary/10",
      link: "/admin/order",
      permission: () => hasPermission("BILL_SHOW"),
    },
    {
      id: 5,
      name: "Tổng giá trị giao dịch",
      value: formatCurrencyVND(
        getFiltedOrders().reduce(
          (total, order) => total + parseFloat(order.totalPrice || 0),
          0
        ) || 0
      ),
      icon: <CurrencyDollarIcon className="h-8 w-8 text-accent" />,
      color: "bg-accent/10",
      link: "/admin/order",
      permission: () =>
        hasPermission("BILL_SHOW") && hasPermission("BILL_SHOW_TOTAL_VALUE"),
    },
  ];

  const getUserPermissions = () => {
    const permissions = localStorage.getItem("permissions");
    return permissions
      ? JSON.parse(permissions)?.map((p) => p.permissionSlug)
      : [];
  };

  const hasPermission = (requiredPermission) => {
    const userId = localStorage.getItem("userId");
    if (userId == 1) {
      return true;
    }
    const userPermissions = getUserPermissions();
    return userPermissions.includes(requiredPermission);
  };

  return (
    <div className="p-4 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-[24px] font-bold">Tổng quan</h1>
          <p className="text-gray-500">
            Chào mừng <strong>{localStorage.getItem("full_name")}</strong> trở
            lại! Hôm nay là ngày {currentDate}
          </p>
        </div>
      </div>

      {/* Thống kê */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 !mb-8">
        {stats.map((stat, idx) => (
          <>
            {stat?.permission() && (
              <Link to={stat?.link} key={`stat${idx}`}>
                <div
                  key={`stats${stat.id}`}
                  className={`card ${stat.color} shadow-sm`}
                >
                  <div className="card-body p-4 flex flex-row items-center justify-between">
                    <div>
                      <p className="text-sm font-medium opacity-70">
                        {stat.name}
                      </p>
                      <h3 className="text-2xl font-bold">{stat.value}</h3>
                    </div>
                    {stat.icon}
                  </div>
                </div>
              </Link>
            )}
          </>
        ))}
        <div className={`card bg-warning/10 shadow-sm`}>
          <div className="card-body p-4 flex flex-row items-center justify-between">
            <div>
              <p className="text-sm font-medium opacity-70">Điểm hoạt động</p>
              <h3 className="text-2xl font-bold">{company?.point || 0}</h3>
            </div>
            <TrophyIcon className="h-8 w-8 text-orange-300" />
          </div>
        </div>
      </div>

      {hasPermission("BILL_SHOW") && (
        <div className="flex items-center space-x-2">
          <select
            id="yearFilter"
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="select select-bordered w-56 select-sm min-w-32"
          >
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="grid !mt-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats2.map((stat, idx) => (
          <>
            {stat?.permission() && (
              <Link to={stat?.link} key={`stat${idx}`}>
                <div
                  key={`stats${stat.id}`}
                  className={`card ${stat.color} shadow-sm`}
                >
                  <div className="card-body p-4 flex flex-row items-center justify-between">
                    <div>
                      <p className="text-sm font-medium opacity-70">
                        {stat.name}
                      </p>
                      <h3 className="text-2xl font-bold">{stat.value}</h3>
                    </div>
                    {stat.icon}
                  </div>
                </div>
              </Link>
            )}
          </>
        ))}
      </div>

      {/* Chức năng quản lý */}
      <div className="card bg-base-100 shadow-md hidden">
        <div className="card-body">
          {/* <h2 className="card-title flex items-center gap-2">
            <HomeIcon className="h-5 w-5 text-primary" />
            Thường sử dụng
          </h2> */}
          <div className=" grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            {features.map((feature) => (
              <Link
                key={`features${feature.id}`}
                to={feature.link}
                className="card bg-base-200 hover:bg-base-300 transition-colors shadow-sm"
              >
                <div className="card-body items-center text-center p-4">
                  <div className="rounded-full bg-primary/10 p-3 mb-2">
                    {feature.icon}
                  </div>
                  <h3 className="font-medium">{feature.name}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {hasPermission("POST_SHOW") && (
        <>
          <div className="card bg-base-100 shadow-md">
            <div className="card-body">
              <h2 className="card-title flex items-center gap-2 mb-3">
                <MegaphoneIcon className="h-5 w-5 text-primary" />
                Thông báo mới
              </h2>
              <div className="overflow-x-auto">
                <table className="table table-zebra w-full">
                  <thead>
                    <tr>
                      <th className="text-[16px]">STT</th>
                      {/* <th>Mã Đơn</th> */}
                      <th className="text-[16px] !normal-case">Tiêu đề</th>
                      <th className="text-[16px] !normal-case">Người viết</th>
                      <th className="text-[16px] !normal-case">Cập nhật</th>
                    </tr>
                  </thead>
                  <tbody>
                    {notifications?.slice(0, 3)?.map((order, index) => (
                      <tr key={order.id}>
                        {/* <td className="font-medium">{order.id}</td> */}
                        <td>{index + 1}</td>
                        <td>{order.title}</td>
                        <td>{order.user?.userFullName}</td>
                        <td>
                          {order.updatedAt
                            ? formatDate(order.updatedAt.toString())
                            : "Chưa cập nhật"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="card-actions justify-end mt-4">
                <Link
                  to="/admin/notification"
                  className="btn btn-outline btn-sm"
                >
                  Xem tất cả thông báo
                </Link>
              </div>
            </div>
          </div>

          <div className="card bg-base-100 shadow-md">
            <div className="card-body">
              <h2 className="card-title flex items-center gap-2 mb-3">
                <NewspaperIcon className="h-5 w-5 text-primary" />
                Bài viết chưa được duyệt
              </h2>
              <div className="overflow-x-auto">
                <table className="table table-zebra w-full">
                  <thead>
                    <tr>
                      <th className="text-[16px]">STT</th>
                      {/* <th>Mã Đơn</th> */}
                      <th className="text-[16px] !normal-case">Tiêu đề</th>
                      <th className="text-[16px] !normal-case">Người viết</th>
                      <th className="text-[16px] !normal-case">Cập nhật</th>
                    </tr>
                  </thead>
                  <tbody>
                    {posts
                      ?.filter((e) => e.status === "draft")
                      .map((order, index) => (
                        <tr key={order.id}>
                          {/* <td className="font-medium">{order.id}</td> */}
                          <td>{index + 1}</td>
                          <td>{order.title}</td>
                          <td>{order.user?.userFullName}</td>
                          <td>
                            {order.updatedAt
                              ? formatDate(order.updatedAt.toString())
                              : "Chưa cập nhật"}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
              <div className="card-actions justify-end mt-4">
                <Link to="/admin/posts" className="btn btn-outline btn-sm">
                  Xem tất cả bài viết
                </Link>
              </div>
            </div>
          </div>
        </>
      )}

      {hasPermission("COMPANY_SHOW") && (
        <div className="card bg-base-100 shadow-md">
          <div className="card-body">
            <h2 className="card-title flex items-center gap-2 mb-3">
              <BuildingOffice2Icon className="h-5 w-5 text-primary" />
              Hội viên chưa được duyệt
            </h2>
            <div className="overflow-x-auto">
              <table className="table table-zebra w-full">
                <thead>
                  <tr>
                    <th className="text-[16px]">STT</th>
                    <th className="text-[16px] !normal-case">Tên đơn vị</th>
                    <th className="text-[16px] !normal-case">
                      Thông tin liên hệ
                    </th>
                    <th className="text-[16px] !normal-case">Cập nhật</th>
                  </tr>
                </thead>
                <tbody>
                  {partners
                    ?.filter((e) => e.approvalStatus === "Đang chờ")
                    ?.slice(0, 3)
                    .map((partner, index) => (
                      <tr key={partner.id}>
                        <td>{index + 1}</td>
                        <td>
                          <div>
                            <p className="font-medium">{partner.companyName}</p>
                            <p className="text-sm text-gray-500">
                              {partner.userFullName}
                            </p>
                          </div>
                        </td>
                        <td>
                          <div>
                            <p>{partner.userPhone}</p>
                            <p className="text-sm text-gray-500">
                              {partner.userEmail}
                            </p>
                          </div>
                        </td>
                        <td>
                          {partner.updatedAt
                            ? formatDate(partner.updatedAt.toString())
                            : "Chưa cập nhật"}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
            <div className="card-actions justify-end mt-4">
              <Link to="/admin/partner" className="btn btn-outline btn-sm">
                Xem tất cả hội viên
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
